import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ZefForm, ZefFormsManagerService } from '@zerops/zef/forms';
import { AppState } from '@zerops/zerops/app';
import { validate, FormState, FormControlState, SetUserDefinedPropertyAction } from 'ngrx-forms';
import { maxLength, pattern, required, greaterThan, lessThanOrEqualTo } from 'ngrx-forms/validation';
import { FEATURE_NAME } from './object-storage-service-stack-form.constant';

// the client's bucket quota maximum comes from settings at runtime, so it's kept
// on the control itself and validated against from there
const DISK_GBYTES_MAX_PROPERTY = 'diskGBytesMax';

export const validateObjectStorageDiskGBytes = (s: FormControlState<number>) => validate(
  s,
  greaterThan(0),
  lessThanOrEqualTo(s.userDefinedProperties?.[DISK_GBYTES_MAX_PROPERTY] ?? Infinity)
);

export const setObjectStorageDiskGBytesMax = (form: ZefForm<{ diskGBytes: number }>, max: number) =>
  new SetUserDefinedPropertyAction(form.getId('diskGBytes'), DISK_GBYTES_MAX_PROPERTY, max);

export interface ObjectStorageServiceStackFormValue {
  name: string;
  diskGBytes: number;
  policy: string;
  rawPolicy?: string;
  cdnEnabled?: boolean;
  // name of the selected object storage, empty means the default of the project location
  location: string;
}

export type ObjectStorageServiceStackFormState = FormState<ObjectStorageServiceStackFormValue>;

@Injectable({ providedIn: 'root' })
export class ObjectStorageServiceStackForm extends ZefForm<ObjectStorageServiceStackFormValue> {
  constructor(
    public store: Store<AppState>,
    public formsManager: ZefFormsManagerService
  ) {
    super(
      store,
      formsManager,
      FEATURE_NAME,
      {
        name: '',
        diskGBytes: 2,
        cdnEnabled: false,
        location: '',
        policy: 'public-read',
        rawPolicy: `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": [
          "*"
        ]
      },
      "Action": [
        "s3:GetBucketLocation",
        "s3:ListBucket"
      ],
      "Resource": [
        "arn:aws:s3:::{{.BucketName}}"
      ]
    },
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": [
          "*"
        ]
      },
      "Action": [
        "s3:GetObject"
      ],
      "Resource": [
        "arn:aws:s3:::{{.BucketName}}/*"
      ]
    }
  ]
}`
      },
      {
        name: validate(required, maxLength(25), pattern(/^[a-z0-9]*$/)),
        diskGBytes: validateObjectStorageDiskGBytes
      }
    );
  }
}
